import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PlaceInput from "../VehicleType/PlaceInput";
import MapContainer from "../VehicleType/MapContainer";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "react-phone-input-2/lib/material.css";
import { getPassengerData, getPassengerToken } from "../../passengerAuth";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import axiosInstance from "../../axiosInstance";
import {
  ArrowLeft, ArrowRight, MapPin, CreditCard, DollarSign, Smartphone,
  Building2, Check, Loader, AlertCircle, CheckCircle, XCircle,
  RefreshCw, Phone, User, Car, Clock, Route, ChevronRight,
  Calendar, Navigation, Zap, X, Search
} from "lucide-react";
import "./Passenger.css";

// ── Steps ──────────────────────────────────────────────────────────
// "form"      → enter pickup / destination / dispatch mode
// "loading"   → waiting for estimatedPrice socket
// "vehicle"   → pick a vehicle
// "searching" → request sent, waiting for driver
// "accepted"  → driver accepted
// "noResults" → no drivers found
// "cancelled" → ride cancelled
// "ended"     → trip completed
// "scheduled" → reservation created

const PAYMENT_OPTIONS = [
  { value: "Cash", label: "Cash", icon: <DollarSign size={15} /> },
  { value: "Card", label: "Card", icon: <CreditCard size={15} /> },
  { value: "Telebirr", label: "Telebirr", icon: <Smartphone size={15} /> },
  { value: "Corporate", label: "Corporate", icon: <Building2 size={15} /> },
];

// ── Haversine distance (km) ─────────────────────────────────────────
function calcDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Hospital Picker Modal ─────────────────────────────────────────
// Fetches all hospitals from DB + sorts by distance from user's location
// Selecting one auto-fills Destination field
function HospitalPickerModal({ onSelect, onClose, pickupCoords }) {
  const [status, setStatus] = useState("idle"); // idle | locating | loading | done | error
  const [hospitals, setHospitals] = useState([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/hospitals`);
      const list = Array.isArray(res.data) ? res.data : [];

      // If we have a reference point (pickup or user location), sort by distance
      let refLat, refLng;
      if (pickupCoords?.lat) {
        refLat = pickupCoords.lat;
        refLng = pickupCoords.lng;
      }

      const enriched = list
        .filter((h) => h.latitude != null && h.longitude != null)
        .map((h) => ({
          ...h,
          distance: refLat != null
            ? calcDistance(refLat, refLng, h.latitude, h.longitude)
            : null,
        }))
        .sort((a, b) =>
          a.distance != null && b.distance != null
            ? a.distance - b.distance
            : 0
        );

      setHospitals(enriched);
      setStatus("done");
    } catch {
      setError("Could not load hospital data. Please try again.");
      setStatus("error");
    }
  }, [pickupCoords]);

  // If no pickup coords yet, try geolocation
  const loadWithGeolocation = useCallback(() => {
    if (pickupCoords?.lat) {
      load();
      return;
    }
    setStatus("locating");
    if (!navigator.geolocation) {
      load(); // fallback: load without distance sorting
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => load(),
      () => load(),
      { timeout: 6000 }
    );
  }, [pickupCoords, load]);

  useEffect(() => {
    loadWithGeolocation();
  }, [loadWithGeolocation]);

  const filtered = hospitals.filter(
    (h) =>
      h.name?.toLowerCase().includes(query.toLowerCase()) ||
      h.location?.toLowerCase().includes(query.toLowerCase()) ||
      h.subCity?.toLowerCase().includes(query.toLowerCase())
  );

  const distLabel = (km) =>
    km == null ? null : km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

  return (
    <div className="pax-modal-backdrop" onClick={onClose}>
      <div className="pax-modal-box" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pax-modal-header">
          <div className="pax-modal-header-icon">
            <Navigation size={20} />
          </div>
          <div>
            <div className="pax-modal-title">Nearest Hospitals</div>
            <div className="pax-modal-subtitle">
              Select a hospital to set as your destination
            </div>
          </div>
          <button className="pax-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="pax-modal-search-wrap">
          <Search size={15} className="pax-modal-search-icon" />
          <input
            type="text"
            className="pax-modal-search"
            placeholder="Search hospitals by name or area…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        {/* Body */}
        <div className="pax-modal-body">
          {(status === "idle" || status === "locating" || status === "loading") && (
            <div className="pax-modal-state">
              <Loader size={28} style={{ animation: "pax-spin 1s linear infinite", color: "#1d4ed8" }} />
              <span>{status === "locating" ? "Getting your location…" : "Loading hospitals…"}</span>
            </div>
          )}

          {status === "error" && (
            <div className="pax-modal-state error">
              <AlertCircle size={26} />
              <span>{error}</span>
              <button className="pax-btn-secondary" style={{ marginTop: 8 }} onClick={loadWithGeolocation}>
                Try Again
              </button>
            </div>
          )}

          {status === "done" && filtered.length === 0 && (
            <div className="pax-modal-state">
              <Navigation size={28} style={{ color: "#94a3b8" }} />
              <span style={{ color: "#94a3b8" }}>No hospitals match your search.</span>
            </div>
          )}

          {status === "done" && filtered.map((h, idx) => (
            <div
              key={h.hospital_id}
              className="pax-hospital-item"
              onClick={() => onSelect(h)}
            >
              <div className="pax-hospital-item-rank">{idx + 1}</div>

              <div className="pax-hospital-item-info">
                <div className="pax-hospital-item-name">{h.name}</div>
                {(h.location || h.subCity) && (
                  <div className="pax-hospital-item-location">
                    <MapPin size={11} />
                    {[h.location, h.subCity].filter(Boolean).join(", ")}
                  </div>
                )}
                <div className="pax-hospital-item-meta">
                  {h.distance != null && (
                    <span className="pax-hospital-dist-badge">
                      📍 {distLabel(h.distance)} away
                    </span>
                  )}
                  {h.classificationofHospitals && (
                    <span className="pax-hospital-class-badge">
                      Class {h.classificationofHospitals}
                    </span>
                  )}
                </div>
              </div>

              <div className="pax-hospital-item-right">
                {h.phoneNumber && (
                  <a
                    href={`tel:${h.phoneNumber}`}
                    className="pax-hospital-phone-link"
                    onClick={(e) => e.stopPropagation()}
                    title="Call"
                  >
                    <Phone size={13} />
                    {h.phoneNumber}
                  </a>
                )}
                <div className="pax-hospital-select-arrow">
                  <ArrowRight size={14} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {status === "done" && (
          <div className="pax-modal-footer">
            <span>{filtered.length} hospital{filtered.length !== 1 ? "s" : ""} found</span>
            <button
              className="pax-btn-secondary"
              style={{ fontSize: "0.78rem", padding: "7px 14px" }}
              onClick={loadWithGeolocation}
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────
export default function PassengerRide({ socket }) {
  const navigate = useNavigate();
  const passenger = getPassengerData();
  const passengerId = passenger?.id?.toString();

  // ── Dispatch mode ──────────────────────────────────────────────────
  const [dispatchMode, setDispatchMode] = useState("dispatch"); // "dispatch" | "schedule"
  const [scheduledAt, setScheduledAt] = useState(null);
  const [scheduleDateError, setScheduleDateError] = useState("");

  // ── Form state ────────────────────────────────────────────────────
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [pickupError, setPickupError] = useState("");
  const [destError, setDestError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const pickupLoc = useRef({});
  const destLoc = useRef({});

  // ── Map coords ─────────────────────────────────────────────────────
  const [startCoords, setStartCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [mapKey, setMapKey] = useState(0);

  // ── Hospital modal ────────────────────────────────────────────────
  const [hospitalModalOpen, setHospitalModalOpen] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);

  // ── Step / booking state ──────────────────────────────────────────
  const [step, setStep] = useState("form");
  const [vehicles, setVehicles] = useState([]);
  const [passIdFromEst, setPassIdFromEst] = useState(null);
  const [acceptedData, setAcceptedData] = useState(null);
  const [selectedVeh, setSelectedVeh] = useState(null);
  const [tripStatus, setTripStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const stepRef = useRef(step);
  const acceptedRef = useRef(false);
  const tripStatusRef = useRef(tripStatus);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    tripStatusRef.current = tripStatus;
  }, [tripStatus]);

  // ── Socket listeners ──────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const onEstimatedPrice = (data) => {
      const list = (data?.byVehicleType || []).map((v) => ({
        id: v.id,
        typeName: v.typeName,
        estimatedPrice: v.estimatedPrice,
        estimatedTime: data.eta,
        distance: data.distance,
        imageUrl: "/car/economyCar.jpg",
      }));
      setPassIdFromEst(data.passengerId || passengerId);
      setVehicles(list);
      setStep("vehicle");
    };

    const onAccepted = (data) => {
      acceptedRef.current = true;
      setAcceptedData(data);
      setStep("accepted");
      toast.success("🎉 A driver has accepted your ride!");
    };

    const onReservationAccepted = (data) => {
      acceptedRef.current = true;
      if (dispatchMode === "schedule") {
        setAcceptedData(data);
        setStep("scheduled");
        toast.success("✅ Your scheduled booking was accepted.");
        return;
      }
      // Fallback: treat as a normal acceptance
      setAcceptedData(data);
      setStep("accepted");
    };

    const onNoResults = () => {
      const currentStep = stepRef.current;
      const currentTripStatus = tripStatusRef.current;
      if (
        acceptedRef.current ||
        currentStep === "accepted" ||
        currentStep === "scheduled" ||
        currentTripStatus === "started" ||
        currentTripStatus === "ended"
      ) {
        console.log("Ignoring noResults after acceptance/active trip");
        return;
      }
      setStep("noResults");
    };

    const onCancelled = (data) => {
      setStep("cancelled");
      toast.error(data?.reasonForCancellation || "Your ride was cancelled.", { autoClose: 6000 });
    };

    const onTripStarted = () => { setTripStatus("started"); toast.info("🚗 Your ride has started!"); };
    const onTripEnded = () => { setTripStatus("ended"); setStep("ended"); toast.success("✅ Trip completed. Thank you!"); };

    socket.on("estimatedPrice", onEstimatedPrice);
    socket.on("requestAccepted", onAccepted);
    socket.on("reservationAccepted", onReservationAccepted);
    socket.on("noResults", onNoResults);
    socket.on("requestCancelled", onCancelled);
    socket.on("tripStarted", onTripStarted);
    socket.on("tripEnded", onTripEnded);

    return () => {
      socket.off("estimatedPrice", onEstimatedPrice);
      socket.off("requestAccepted", onAccepted);
      socket.off("reservationAccepted", onReservationAccepted);
      socket.off("noResults", onNoResults);
      socket.off("requestCancelled", onCancelled);
      socket.off("tripStarted", onTripStarted);
      socket.off("tripEnded", onTripEnded);
    };
  }, [socket, passengerId, dispatchMode]);

  // ── Location handlers ─────────────────────────────────────────────
  const handlePickupChange = useCallback(async (value) => {
    setPickupError("");
    const address = value?.label ?? value;
    setPickup(address);
    if (!address) return;
    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      pickupLoc.current = latLng;
      setStartCoords(latLng);
    } catch {
      pickupLoc.current = {};
      setStartCoords(null);
      setPickupError("Could not locate this address.");
    }
  }, []);

  const handleDestinationChange = useCallback(async (value) => {
    setDestError("");
    const address = value?.label ?? value;
    setDestination(address);
    if (!address) { destLoc.current = {}; setDestCoords(null); return; }
    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      destLoc.current = latLng;
      setDestCoords(latLng);
    } catch {
      destLoc.current = {};
      setDestCoords(null);
      setDestError("Could not locate this address.");
    }
  }, []);

  // ── Hospital selection → auto-fill destination ────────────────────
  const handleHospitalSelect = useCallback(async (hospital) => {
    setHospitalModalOpen(false);
    setSelectedHospital(hospital);

    const address = [hospital.name, hospital.location, hospital.subCity]
      .filter(Boolean)
      .join(", ");

    setDestError("");
    setDestination(address);

    // geocode to get coords
    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      destLoc.current = latLng;
      setDestCoords(latLng);
    } catch {
      // fallback: use stored lat/lng from DB directly
      if (hospital.latitude && hospital.longitude) {
        const latLng = { lat: parseFloat(hospital.latitude), lng: parseFloat(hospital.longitude) };
        destLoc.current = latLng;
        setDestCoords(latLng);
      }
    }
  }, []);

  const clearHospital = useCallback(() => {
    setSelectedHospital(null);
    setDestination("");
    setDestCoords(null);
    destLoc.current = {};
    setDestError("");
  }, []);

  // ── Schedule booking ──────────────────────────────────────────────
  // handleScheduleSubmit is now called from handleVehicleSelect after vehicle type is chosen

  // ── Phone helper ──────────────────────────────────────────────────
  const handlePhoneChange = (value) => {
    setPhoneError("");
    const withPlus = value ? (value.startsWith("+") ? value : "+" + value) : value;
    setPhone(withPlus);
  };

  // Resolve passengerId from phone (booking on behalf of someone else)
  const resolvePassengerByPhone = async () => {
    const normalizedPhone = phone.startsWith("+") ? phone : `+${phone}`;
    try {
      const passengerToken = getPassengerToken();
      const res = await axiosInstance.get("/passengers/by-phone", {
        params: { phoneNumber: normalizedPhone },
        headers: passengerToken
          ? { Authorization: `Bearer ${passengerToken}` }
          : undefined,
      });
      const resolvedId = res?.data?.passenger?.id;
      if (!resolvedId) {
        toast.error("Passenger not found for this phone number.");
        return null;
      }
      return resolvedId.toString();
    } catch {
      toast.error("Could not look up passenger by phone number.");
      return null;
    }
  };

  // ── Form submit ───────────────────────────────────────────────────
  const handleGetEstimate = async (e) => {
    e.preventDefault();

    setPickupError("");
    setDestError("");
    setPhoneError("");
    setScheduleDateError("");
    let valid = true;

    if (!pickup || !pickupLoc.current?.lat) { setPickupError("Please select a valid pickup location."); valid = false; }
    if (!destination || !destLoc.current?.lat) { setDestError("Please select a valid destination."); valid = false; }
    if (!phone) { setPhoneError("Phone number is required."); valid = false; }
    if (!valid) return;

    // Normalize phone
    const normalizedPhone = phone.startsWith("+") ? phone : `+${phone}`;
    // Basic length check
    const stripped = normalizedPhone.replace(/\D/g, "");
    if (stripped.length < 7 || stripped.length > 15) {
      setPhoneError("Enter a valid phone number.");
      return;
    }

    if (dispatchMode === "schedule") {
      // Validate date first, then show vehicle selection (same as dispatch-now)
      if (!scheduledAt) {
        setScheduleDateError("Please select a pickup date and time.");
        return;
      }
      if (scheduledAt <= new Date()) {
        setScheduleDateError("Pickup time must be in the future.");
        return;
      }
    }

    if (!socket || !socket.connected) {
      toast.error("No connection to dispatch service. Please refresh.");
      return;
    }

    setStep("loading");
    socket.emit("getEstimatedPriceWeb", {
      pickUpLocationLatitude: pickupLoc.current.lat.toString(),
      pickUpLocationLongitude: pickupLoc.current.lng.toString(),
      destinationLocationLatitude: destLoc.current.lat.toString(),
      destinationLocationLongitude: destLoc.current.lng.toString(),
      pickUpPlaceName: pickup,
      destinationPlaceName: destination,
    });
  };

  // ── Vehicle select → dispatch-now uses requestWeb, schedule uses reservation ──
  const handleVehicleSelect = async (vt) => {
    setSelectedVeh(vt);
    acceptedRef.current = false;
    setStep("searching");

    const resolvedId = await resolvePassengerByPhone();
    if (!resolvedId) { setStep("vehicle"); return; }

    const normalizedPhone = phone.startsWith("+") ? phone : `+${phone}`;

    if (dispatchMode === "schedule") {
      // Scheduled booking — include vehicleTypeId so dispatch doesn't get NaN
      const payload = {
        passengerId: resolvedId,
        pickUpLocationLatitude: pickupLoc.current.lat.toString(),
        pickUpLocationLongitude: pickupLoc.current.lng.toString(),
        destinationLocationLatitude: destLoc.current.lat.toString(),
        destinationLocationLongitude: destLoc.current.lng.toString(),
        vehicleTypeId: vt.id.toString(),
        pickUpDate: scheduledAt.toISOString(),
        destinationLocationName: destination,
        phone: normalizedPhone,
        paymentMethod,
      };
      socket.emit("reservation", JSON.stringify(payload));
      toast.info("Searching for a driver to accept your scheduled booking...");
    } else {
      // Immediate dispatch
      const payload = {
        pickUpLocationLatitude: pickupLoc.current.lat.toString(),
        pickUpLocationLongitude: pickupLoc.current.lng.toString(),
        destinationLocationLatitude: destLoc.current.lat.toString(),
        destinationLocationLongitude: destLoc.current.lng.toString(),
        vehicleTypeId: vt.id.toString(),
        phone: normalizedPhone,
        rideSharing: "Off",
        passengerId: resolvedId,
        destinationPlaceName: destination,
        paymentMethod,
      };
      socket.emit("requestWeb", payload);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────
  const handleReset = () => {
    setPickup("");
    setDestination("");
    setPickupError("");
    setDestError("");
    setPhone("");
    setPhoneError("");
    setPaymentMethod("Cash");
    setVehicles([]);
    setSelectedVeh(null);
    setAcceptedData(null);
    setTripStatus(null);
    acceptedRef.current = false;
    setStep("form");
    setDispatchMode("dispatch");
    setScheduledAt(null);
    setScheduleDateError("");
    setSubmitting(false);
    setStartCoords(null);
    setDestCoords(null);
    setSelectedHospital(null);
    setMapKey((k) => k + 1);
    pickupLoc.current = {};
    destLoc.current = {};
  };

  // ─────────────────────────────────────────────────────────────────
  //  BREADCRUMB
  // ─────────────────────────────────────────────────────────────────
  const renderBreadcrumb = () => {
    const steps = [
      { key: "form", label: "Route" },
      { key: "vehicle", label: "Vehicle" },
      { key: "searching", label: "Confirm" },
    ];
    const activeIdx =
      step === "loading" ? 1 :
        step === "vehicle" ? 1 :
          (step === "accepted" || step === "noResults" || step === "cancelled" || step === "ended" || step === "searching") ? 2 : 0;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: "0.82rem", fontWeight: i <= activeIdx ? 700 : 500,
              color: i < activeIdx ? "#16a34a" : i === activeIdx ? "#1d4ed8" : "#94a3b8",
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%", display: "flex",
                alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700,
                background: i < activeIdx ? "#dcfce7" : i === activeIdx ? "#dbeafe" : "#f1f5f9",
                color: i < activeIdx ? "#16a34a" : i === activeIdx ? "#1d4ed8" : "#94a3b8",
                border: `2px solid ${i < activeIdx ? "#86efac" : i === activeIdx ? "#93c5fd" : "#e2e8f0"}`,
              }}>
                {i < activeIdx ? <Check size={12} /> : i + 1}
              </div>
              {s.label}
            </div>
            {i < steps.length - 1 && <ChevronRight size={14} color="#cbd5e1" />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  //  FORM STEP
  // ─────────────────────────────────────────────────────────────────
  const renderForm = () => (
    <form onSubmit={handleGetEstimate}>
      {/* Two-column layout: form left, map right (like Dispatch.js BookingLayout) */}
      <div className="pax-dispatch-grid">

        {/* ── LEFT: Form ── */}
        <div className="pax-dispatch-form-col">

          {/* Dispatch Mode Toggle */}
          <div className="pax-mode-toggle-card">
            <div className="pax-mode-toggle-label">Select Booking Type</div>
            <div className="pax-mode-toggle-row">
              <button
                type="button"
                className={`pax-mode-btn ${dispatchMode === "dispatch" ? "active" : ""}`}
                onClick={() => { setDispatchMode("dispatch"); setScheduleDateError(""); setScheduledAt(null); }}
                id="pax-mode-dispatch"
              >
                <Zap size={15} /> Dispatch Now
              </button>
              <button
                type="button"
                className={`pax-mode-btn ${dispatchMode === "schedule" ? "active schedule" : ""}`}
                onClick={() => setDispatchMode("schedule")}
                id="pax-mode-schedule"
              >
                <Calendar size={15} /> Schedule Booking
              </button>
            </div>
          </div>

          {/* Booking details card */}
          <div className="pax-card">
            <h3 className="pax-card-title"><MapPin size={18} /> Your Booking Details</h3>

            {/* Pickup */}
            <div className="pax-field" style={{ marginBottom: 16 }}>
              <label>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: "#22c55e", borderRadius: "50%", display: "inline-block" }} />
                  Pickup Location
                </span>
              </label>
              <PlaceInput value={pickup} onChanges={handlePickupChange} point={pickup} error={pickupError} />
              {pickupError && <div className="pax-field-error"><AlertCircle size={13} /> {pickupError}</div>}
            </div>

            {/* Destination */}
            <div className="pax-field" style={{ marginBottom: 10 }}>
              <label>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: "#ef4444", borderRadius: "50%", display: "inline-block" }} />
                  Destination
                </span>
              </label>
              <PlaceInput
                value={destination}
                onChanges={handleDestinationChange}
                point={destination}
                error={destError}
                disabled={selectedHospital !== null}
              />
              {destError && <div className="pax-field-error"><AlertCircle size={13} /> {destError}</div>}

              {/* Selected hospital chip */}
              {selectedHospital && (
                <div className="pax-selected-hospital-chip">
                  <Navigation size={14} style={{ color: "#1d4ed8", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedHospital.name}
                    </div>
                    {selectedHospital.phoneNumber && (
                      <a href={`tel:${selectedHospital.phoneNumber}`} className="pax-selected-hospital-phone" onClick={(e) => e.stopPropagation()}>
                        <Phone size={11} /> {selectedHospital.phoneNumber}
                      </a>
                    )}
                  </div>
                  <button type="button" className="pax-selected-hospital-remove" onClick={clearHospital} title="Remove hospital">
                    <X size={13} />
                  </button>
                </div>
              )}
            </div>

            {/* Find Nearest Hospitals button */}
            <button
              type="button"
              className="pax-find-hospitals-btn"
              onClick={() => setHospitalModalOpen(true)}
              id="pax-find-hospitals-btn"
            >
              <Navigation size={16} />
              {selectedHospital ? "Change Hospital" : "Find Nearest Hospitals"}
            </button>

            {/* Phone number */}
            <div className="pax-field" style={{ marginTop: 16 }}>
              <label style={{ fontSize: "0.85rem", fontWeight: 600, color: "#334155", marginBottom: 7, display: "flex", alignItems: "center", gap: 6 }}>
                <Phone size={13} style={{ color: "#1d4ed8" }} />
                Passenger Phone Number
              </label>
              <PhoneInput
                country="et"
                value={phone}
                onChange={handlePhoneChange}
                enableAreaCodes={true}
                containerClass="pax-phone-container"
                inputClass="pax-phone-input"
                buttonClass="pax-phone-flag"
                placeholder="Enter passenger's phone"
              />
              {phoneError && (
                <div className="pax-field-error" style={{ marginTop: 6 }}>
                  <AlertCircle size={13} /> {phoneError}
                </div>
              )}
              <div className="pax-hint" style={{ fontSize: "0.76rem", color: "#94a3b8", marginTop: 4 }}>
                Enter the phone number of the person being transported
              </div>
            </div>

            {/* Schedule date/time picker */}
            {dispatchMode === "schedule" && (
              <div className="pax-schedule-field" style={{ marginTop: 16 }}>
                <label className="pax-field" style={{ marginBottom: 8 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", fontWeight: 600, color: "#334155" }}>
                    <Calendar size={13} style={{ color: "#0d9488" }} />
                    Pickup Date & Time
                  </span>
                </label>
                <DatePicker
                  selected={scheduledAt}
                  onChange={(d) => { setScheduleDateError(""); setScheduledAt(d); }}
                  showTimeSelect
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={new Date()}
                  placeholderText="Select pickup date & time"
                  className="pax-datepicker-input"
                />
                {scheduleDateError && (
                  <div className="pax-field-error" style={{ marginTop: 6 }}><AlertCircle size={13} /> {scheduleDateError}</div>
                )}
              </div>
            )}
          </div>

          {/* Payment method (dispatch mode only) */}
          {dispatchMode === "dispatch" && (
            <div className="pax-card">
              <h3 className="pax-card-title"><CreditCard size={18} /> Payment Method</h3>
              <div className="pax-payment-grid">
                {PAYMENT_OPTIONS.map((opt) => (
                  <div className="pax-payment-option" key={opt.value}>
                    <input
                      type="radio" id={`pay-${opt.value}`} name="paymentMethod"
                      value={opt.value} checked={paymentMethod === opt.value}
                      onChange={() => setPaymentMethod(opt.value)}
                    />
                    <label htmlFor={`pay-${opt.value}`} className="pax-payment-label">
                      {opt.icon} {opt.label}
                      {paymentMethod === opt.value && <Check size={14} style={{ marginLeft: "auto", color: "#1d4ed8" }} />}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Passenger info + action buttons */}
          <div className="pax-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <User size={16} color="#94a3b8" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#475569" }}>
                  {passenger?.firstName} {passenger?.lastName}
                </div>
                <div style={{ fontSize: "0.76rem", color: "#94a3b8" }}>{passenger?.phoneNumber || ""}</div>
              </div>
              <span style={{ fontSize: "0.72rem", background: "#dcfce7", color: "#166534", padding: "3px 8px", borderRadius: "100px", fontWeight: 700 }}>
                VERIFIED
              </span>
            </div>

            <div className="pax-action-row" style={{ paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
              <button type="button" className="pax-btn-secondary" onClick={handleReset}>Clear</button>
              <button
                type="submit"
                className={`pax-btn-primary ${dispatchMode === "schedule" ? "schedule" : ""}`}
                disabled={!pickup || !destination || submitting}
                id="pax-get-estimate-btn"
              >
                {submitting ? (
                  <><Loader size={15} style={{ animation: "pax-spin 1s linear infinite" }} /> Scheduling…</>
                ) : dispatchMode === "schedule" ? (
                  <><Calendar size={15} /> Schedule Booking</>
                ) : (
                  <>See Vehicles <ArrowRight size={16} /></>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Live Map ── */}
        <div className="pax-dispatch-map-col">
          <MapContainer
            key={mapKey}
            startPoint={startCoords}
            destination={destCoords}
          />
        </div>
      </div>
    </form>
  );

  // ─────────────────────────────────────────────────────────────────
  //  LOADING (estimating prices)
  // ─────────────────────────────────────────────────────────────────
  const renderLoading = () => (
    <div className="pax-status-panel searching">
      <div className="pax-status-header">
        <div className="pax-status-icon">
          <Loader size={26} style={{ animation: "pax-spin 1s linear infinite" }} />
        </div>
        <div>
          <p className="pax-status-title">Calculating prices…</p>
          <p className="pax-status-sub">Finding available vehicle types and estimated costs for your route.</p>
        </div>
      </div>
      <div className="pax-search-animation">
        <div className="pax-search-dots"><span /><span /><span /></div>
        <strong style={{ color: "#1d4ed8" }}>{pickup}</strong> → <strong style={{ color: "#1d4ed8" }}>{destination}</strong>
      </div>
      <button className="pax-btn-secondary" style={{ width: "fit-content" }} onClick={handleReset}>Cancel</button>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────
  //  VEHICLE SELECTION
  // ─────────────────────────────────────────────────────────────────
  const renderVehicleSelection = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Route summary */}
      <div style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        {vehicles[0]?.distance && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Route size={18} color="#1d4ed8" />
            <div>
              <div style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Distance</div>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>{vehicles[0].distance}</div>
            </div>
          </div>
        )}
        {vehicles[0]?.estimatedTime && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={18} color="#1d4ed8" />
            <div>
              <div style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>ETA</div>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>{vehicles[0].estimatedTime}</div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MapPin size={18} color="#22c55e" />
          <div>
            <div style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>From → To</div>
            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.88rem" }}>
              {pickup} <span style={{ color: "#cbd5e1" }}>→</span> {destination}
            </div>
          </div>
        </div>
        <button className="pax-btn-secondary" style={{ marginLeft: "auto", fontSize: "0.82rem" }} onClick={() => setStep("form")}>
          ← Change Route
        </button>
      </div>

      <div>
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 14, fontSize: "1.05rem" }}>Choose your vehicle</div>
        <div className="pax-vehicle-estimate-grid">
          {vehicles.map((vt, idx) => (
            <button
              key={vt.id}
              className="pax-vehicle-estimate-card"
              onClick={() => handleVehicleSelect(vt)}
              id={`pax-vehicle-${vt.id}`}
            >
              {idx === 0 && vt.typeName !== "BLS Ambulance" && (
                <span className="pax-vehicle-recommend-tag">⚡ Fastest</span>
              )}
              {vt.imageUrl && (
                <img src={vt.imageUrl} alt={vt.typeName} className="pax-vehicle-estimate-img" onError={(e) => { e.target.style.display = "none"; }} />
              )}
              <div className="pax-vehicle-est-icon" style={vt.imageUrl ? { display: "none" } : {}}><Car size={28} /></div>
              <div className="pax-vehicle-est-name">{vt.typeName}</div>
              <div className="pax-vehicle-est-eta"><Clock size={12} /> {vt.estimatedTime || "—"}</div>
              <div className="pax-vehicle-est-price">{vt.estimatedPrice ? `${vt.estimatedPrice} ETB` : "—"}</div>
              <div className="pax-vehicle-est-select">Select <ArrowRight size={14} /></div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: "0.8rem", color: "#94a3b8", textAlign: "center" }}>
        Prices shown are estimates and may vary based on actual distance and traffic.
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────
  //  STATUS PANELS
  // ─────────────────────────────────────────────────────────────────
  const renderStatus = () => {
    if (step === "searching") return (
      <div className="pax-status-panel searching">
        <div className="pax-status-header">
          <div className="pax-status-icon"><Loader size={26} style={{ animation: "pax-spin 1s linear infinite" }} /></div>
          <div style={{ flex: 1 }}>
            <p className="pax-status-title">Searching for drivers…</p>
            <p className="pax-status-sub">
              {dispatchMode === "schedule"
                ? "Looking for an available driver to accept your scheduled booking. Please stay on this page."
                : `Looking for the nearest ${selectedVeh?.typeName} driver. Please stay on this page.`}
            </p>
          </div>
        </div>
        {dispatchMode !== "schedule" && selectedVeh && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "rgba(255,255,255,0.6)", borderRadius: 10 }}>
            <Car size={18} color="#1d4ed8" />
            <span style={{ fontWeight: 600, color: "#0f172a" }}>{selectedVeh.typeName}</span>
            {selectedVeh.estimatedPrice && <span style={{ fontSize: "0.85rem", color: "#64748b" }}>· Est. {selectedVeh.estimatedPrice} ETB</span>}
          </div>
        )}
        <div className="pax-search-animation">
          <div className="pax-search-dots"><span /><span /><span /></div>
          {dispatchMode === "schedule"
            ? "Waiting for a driver to accept your scheduled booking"
            : "Matching you with the nearest available driver"}
        </div>
      </div>
    );

    if (step === "accepted") {
      const d = acceptedData?.driver || acceptedData;
      const initials = `${d?.firstName?.[0] || ""}${d?.lastName?.[0] || ""}`.toUpperCase() || "D";
      const statusLabel = tripStatus === "started" ? "Your ride has started!" : "Driver on the way!";
      const statusSub = tripStatus === "started"
        ? "Sit back and relax. Your driver is taking you to your destination."
        : "Your driver has accepted the request and will call you shortly.";
      return (
        <div className={`pax-status-panel ${tripStatus === "started" ? "started" : "accepted"}`}>
          <div className="pax-status-header">
            <div className="pax-status-icon"><CheckCircle size={26} /></div>
            <div style={{ flex: 1 }}><p className="pax-status-title">{statusLabel}</p><p className="pax-status-sub">{statusSub}</p></div>
            <span className={`pax-trip-badge ${tripStatus === "started" ? "started" : "on-way"}`}>{tripStatus === "started" ? "In Progress" : "Accepted"}</span>
          </div>
          <div className="pax-driver-card">
            <div className="pax-driver-avatar">{initials}</div>
            <div style={{ flex: 1 }}>
              <p className="pax-driver-name">{d ? `${d.firstName || ""} ${d.lastName || ""}`.trim() : "Your Driver"}</p>
              {d?.phoneNumber && <div className="pax-driver-phone"><Phone size={13} /> {d.phoneNumber}</div>}
              {selectedVeh?.typeName && <div className="pax-driver-vehicle"><Car size={13} /> {selectedVeh.typeName}</div>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="pax-btn-secondary" onClick={handleReset} style={{ fontSize: "0.82rem" }}>Book Another Ride</button>
          </div>
        </div>
      );
    }

    if (step === "noResults") return (
      <div className="pax-status-panel noResults">
        <div className="pax-status-header">
          <div className="pax-status-icon"><AlertCircle size={26} /></div>
          <div>
            <p className="pax-status-title">No drivers found nearby</p>
            <p className="pax-status-sub">
              {dispatchMode === "schedule"
                ? "No available drivers accepted your scheduled booking. Please try a different time."
                : "No available drivers matched your request. Try a different vehicle."}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {dispatchMode === "schedule" ? (
            <button className="pax-btn-secondary" onClick={() => setStep("form")}>Change Time</button>
          ) : (
            <button className="pax-btn-secondary" onClick={() => setStep("vehicle")}>Try Different Vehicle</button>
          )}
          <button className="pax-btn-primary" onClick={handleReset}><RefreshCw size={15} /> Start Over</button>
        </div>
      </div>
    );

    if (step === "cancelled") return (
      <div className="pax-status-panel cancelled">
        <div className="pax-status-header">
          <div className="pax-status-icon"><XCircle size={26} /></div>
          <div><p className="pax-status-title">Ride cancelled</p><p className="pax-status-sub">Your ride request was cancelled. You can submit a new request.</p></div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="pax-btn-primary" onClick={handleReset}><RefreshCw size={15} /> New Request</button>
        </div>
      </div>
    );

    if (step === "ended") return (
      <div className="pax-status-panel ended">
        <div className="pax-status-header">
          <div className="pax-status-icon"><CheckCircle size={26} /></div>
          <div><p className="pax-status-title">Trip completed ✓</p><p className="pax-status-sub">Your trip has ended. Thank you for using ለAmbulance!</p></div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="pax-btn-secondary" onClick={() => navigate("/passenger/trips")}>View Trip History</button>
          <button className="pax-btn-primary" onClick={handleReset}>Book Another Ride</button>
        </div>
      </div>
    );

    if (step === "scheduled") return (
      <div className="pax-status-panel ended">
        <div className="pax-status-header">
          <div className="pax-status-icon"><Calendar size={26} /></div>
          <div>
            <p className="pax-status-title">Booking Scheduled ✓</p>
            <p className="pax-status-sub">
              Your ride has been scheduled for{" "}
              <strong>{scheduledAt ? scheduledAt.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "your selected time"}</strong>.
              A driver has accepted your booking and will arrive at the scheduled time.
            </p>
          </div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: 12, padding: "14px 18px", display: "flex", flexDirection: "column", gap: 8, border: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", gap: 8, fontSize: "0.85rem", color: "#334155" }}>
            <MapPin size={15} color="#22c55e" style={{ flexShrink: 0, marginTop: 1 }} />
            <span><strong>From:</strong> {pickup}</span>
          </div>
          <div style={{ display: "flex", gap: 8, fontSize: "0.85rem", color: "#334155" }}>
            <MapPin size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
            <span><strong>To:</strong> {destination}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="pax-btn-secondary" onClick={() => navigate("/passenger/trips")}>View Booking History</button>
          <button className="pax-btn-primary" onClick={handleReset}><RefreshCw size={15} /> New Booking</button>
        </div>
      </div>
    );

    return null;
  };

  // ─────────────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer position="top-right" theme="light" />

      {/* Hospital Picker Modal */}
      {hospitalModalOpen && (
        <HospitalPickerModal
          pickupCoords={pickupLoc.current}
          onSelect={handleHospitalSelect}
          onClose={() => setHospitalModalOpen(false)}
        />
      )}

      <div className="pax-ride-wrapper">
        {/* PAGE HEADER */}
        <div className="pax-page-header">
          <button className="pax-back-btn" onClick={() => navigate("/passenger")}>
            <ArrowLeft size={15} /> Back
          </button>
          <div>
            <h1 className="pax-page-title">Non-Emergency Service</h1>
            <p className="pax-page-subtitle">
              {dispatchMode === "schedule"
                ? "Schedule a medical transport in advance — pick your preferred date and time."
                : "Safe, reliable medical transport — enter your route to see available vehicles and prices."}
            </p>
          </div>
        </div>

        {/* Breadcrumb */}
        {(step === "form" || step === "loading" || step === "vehicle") && dispatchMode === "dispatch" && renderBreadcrumb()}

        {/* Step content */}
        {step === "form" && renderForm()}
        {step === "loading" && renderLoading()}
        {step === "vehicle" && renderVehicleSelection()}
        {(step === "searching" || step === "accepted" || step === "noResults" || step === "cancelled" || step === "ended" || step === "scheduled") && renderStatus()}
      </div>
    </>
  );
}
